import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError, retry } from "@reduxjs/toolkit/query/react";

import { RootState } from "@/store/hooks";

import { createForbiddenError, handleApiError } from "./errorHandler";
import { generateRequestMetadata, logRequest, logResponse } from "./interceptors";
import { requestMonitor } from "./monitor";
import { applyRoleBasedModifications } from "./transformers";
import { ExtraOptions } from "./types";

const baseUrl = import.meta.env.VITE_APP_API_URL;

type BaseQuery = BaseQueryFn<FetchArgs | string, unknown, FetchBaseQueryError, ExtraOptions>;

/**
 * Base query with retry logic
 * Retries failed requests once by default
 */
const baseQueryWithRetry = retry<BaseQuery>(
  async (args, api, extraOptions) => {
    const version = extraOptions?.version;
    const dynamicBaseUrl = version ? `${baseUrl}/${version}` : baseUrl;

    const dynamicBaseQuery = fetchBaseQuery({
      baseUrl: dynamicBaseUrl,
      prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).auth.token;
        if (token) {
          headers.set("Authorization", `Bearer ${token}`);
        }
        return headers;
      }
    });

    return dynamicBaseQuery(args, api, extraOptions);
  },
  { maxRetries: 1 }
);

/**
 * Main base query with authentication, logging, and role-based URL modification
 */
export const baseQuery: BaseQuery = async (args, api, extraOptions) => {
  // Generate request metadata for logging
  const metadata = generateRequestMetadata();

  // Get auth state
  const { auth } = api.getState() as RootState;
  const userRole = auth.role;
  const shippingCompanyId = auth.shippingCompanyId;
  const companyId = auth.company?.id || undefined;
  const userId = auth.id;

  // Apply role-based URL modifications
  let modifiedArgs: FetchArgs | string | null = args;

  if (userRole) {
    modifiedArgs = applyRoleBasedModifications(args, userRole, companyId, shippingCompanyId, userId);

    // If modification returned null, abort the request with 403
    if (modifiedArgs === null) {
      return createForbiddenError();
    }
  }

  // Log request
  logRequest(modifiedArgs, metadata, false);
  // Monitor request
  requestMonitor.logRequest(modifiedArgs, metadata.requestId, metadata.timestamp);
  // Execute request with retry logic
  const result = await baseQueryWithRetry(modifiedArgs, api, extraOptions);

  // Log response
  logResponse(result, metadata, false);
  // Monitor response
  requestMonitor.logResponse(result, metadata.requestId, metadata.timestamp);
  // Handle errors (401, 403, etc.)
  const handledResult = handleApiError(result.error, api, result);

  // Attach metadata to result
  return {
    ...handledResult,
    meta: { ...handledResult.meta, ...metadata }
  };
};
